import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Tag, message } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { billingWeightRuleApi, type BillingWeightRuleDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

const BillingWeightRuleManage: React.FC = () => {
  const [list, setList] = useState<BillingWeightRuleDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    billingWeightRuleApi
      .list()
      .then((res) => setList(res.data || []))
      .catch(() => {
        setList([]);
        message.warning('无法加载计费重规则列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (r: BillingWeightRuleDTO) => {
    setEditingId(r.id);
    form.setFieldsValue({ name: r.name, ruleText: r.ruleText });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload = {
      name: values.name as string,
      ruleText: values.ruleText as string,
    };
    setSaving(true);
    const req = editingId
      ? billingWeightRuleApi.update(editingId, payload)
      : billingWeightRuleApi.create(payload);
    req
      .then((res) => {
        const d = res.data;
        const cascade = d?.cascadeRecalcCount;
        const status = d?.compileStatus;
        let msg = editingId ? '更新成功' : '创建成功';
        if (status === 'FAILED') {
          msg += `（解析失败：${d.compileError || '未知错误'}）`;
        } else if (status === 'OK') {
          msg += '（已解析公式';
          if (typeof cascade === 'number') msg += `，级联重算 ${cascade} 单`;
          msg += '）';
        }
        message.success(msg);
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSaving(false));
  };

  const onDelete = (id: string) => {
    billingWeightRuleApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const statusTag = (s?: string) => {
    if (s === 'OK') return <Tag color="green">OK</Tag>;
    if (s === 'FAILED') return <Tag color="red">FAILED</Tag>;
    if (s === 'PENDING') return <Tag color="orange">PENDING</Tag>;
    return <Tag>{s || '-'}</Tag>;
  };

  return (
    <>
      <Card
        title="计费重计算规则"
        extra={
          <Button type="primary" onClick={openAdd}>
            新增规则
          </Button>
        }
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          规则正文将由当前启用的大模型解析为结构化公式；编辑正文后会重新解析并对未确认计费重的运单级联重算。
        </p>
        <AdaptiveTable
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '名称', dataIndex: 'name', width: 160 },
            {
              title: '规则正文',
              dataIndex: 'ruleText',
              ellipsis: true,
              render: (v: string) => (v && v.length > 80 ? `${v.slice(0, 80)}…` : v),
            },
            {
              title: '解析状态',
              dataIndex: 'compileStatus',
              width: 100,
              render: (v: string, r) => (
                <span title={r.compileError || undefined}>{statusTag(v)}</span>
              ),
            },
            { title: '公式版本', dataIndex: 'formulaVersion', width: 90 },
            {
              title: '更新时间',
              dataIndex: 'updatedAt',
              width: 170,
              render: (v: string) => (v ? String(v).replace('T', ' ').slice(0, 19) : '-'),
            },
            {
              title: '操作',
              width: 140,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该规则？" onConfirm={() => onDelete(r.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑计费重规则' : '新增计费重规则'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="例如：取大（系数5000）" />
          </Form.Item>
          <Form.Item
            name="ruleText"
            label="规则"
            rules={[{ required: true, message: '请输入规则正文' }]}
            extra="保存时将调用已启用大模型解析为公式；仅改名称不会重解析。"
          >
            <Input.TextArea rows={6} placeholder="例如：每箱取实重与材积重较大者，材积重=长×宽×高(cm)/5000，再对各箱求和" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存并解析
              </Button>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BillingWeightRuleManage;
